// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'

// swagger-ui
import SwaggerUI from 'swagger-ui'
import 'swagger-ui/dist/swagger-ui.css'

// semantic-ui
import { Container, Header, Icon } from 'semantic-ui-react'

// services
import { updateUsagePlansAndApisList, getApi } from 'services/api-catalog'

// components
import ApisMenu from 'components/ApisMenu'
import SwaggerLayoutPlugin from 'components/SwaggerUiLayout'
import PageWithSidebar from 'components/PageWithSidebar'

// state
import { store } from 'services/state.js'
import { observer } from 'mobx-react'

export default observer(class ApiPage extends React.Component {
  containerRef = React.createRef()
  removed = false

  componentDidMount () { this.updateApi().then(() => updateUsagePlansAndApisList(true)) }
  componentDidUpdate () { this.updateApi() }
  componentWillUnmount () { this.removed = true }

  updateApi () {
    return getApi(this.props.match.params.apiId || 'ANY', true, this.props.match.params.stage)
      .then(api => {
        const pusher = document.getElementById('pusher') // Main page section displaying apis
        pusher.style.overflow = 'auto' // Any time a new API is selected reset the pusher scrollbar to enabled

        if (api && !this.removed) {
          const cell = {
            shouldPreauthorizeApiKey: false,
            preauthorizeApiKey: () => {
              cell.shouldPreauthorizeApiKey = true
            },
            authDialogHandler: () => {
              // When the Authorize button is clicked bringing up the authorization dialog, scroll the pusher section to the top of the page and disable scrolling
              const authButton = document.querySelector('button.authorize')
              authButton.onclick = () => {
                pusher.scrollTo(0, 0)
                pusher.style.overflow = 'unset'
              }

              // When the authorization dialog is removed from the DOM, re-enable scrolling on the pusher section
              var authWrapper = document.getElementsByClassName('auth-wrapper') // Parent of the authorization dialog HTML elements
              if (authWrapper.length) {
                authWrapper = authWrapper[0]

                const observer = new MutationObserver((parent) => {
                  parent[0].removedNodes.forEach((child) => {
                    // When the element with the dialog-ux class is removed from the DOM, re-enable scrolling on the pusher section
                    child.className === 'dialog-ux' && (pusher.style.overflow = 'auto')
                  })
                })
                observer.observe(authWrapper, { childList: true })
              }
            }
          }

          const swaggerUiConfig = {
            domNode: this.containerRef.current,
            plugins: [SwaggerLayoutPlugin],
            supportedSubmitMethods: [],
            spec: api.swagger,
            onComplete: () => {
              cell.preauthorizeApiKey()
              cell.authDialogHandler()
            }
          }
          delete swaggerUiConfig.supportedSubmitMethods

          const uiHandler = SwaggerUI(swaggerUiConfig)
          cell.preauthorizeApiKey = () => {
            if (store.apiKey) {
              uiHandler.preauthorizeApiKey('api_key', store.apiKey)
            }
          }
          if (cell.shouldPreauthorizeApiKey) {
            cell.preauthorizeApiKey()
          }
        }
      })
  }

  render () {
    let errorHeader
    let errorBody

    if (store.apiList.loaded) {
      if (!store.apiList.apiGateway.length && !store.apiList.generic.length) {
        errorHeader = 'No APIs Published'
        errorBody = 'Your administrator hasn\'t added any APIs to your account. Please contact them to publish an API.'
      } else if (!store.api) {
        errorHeader = 'No Such API'
        errorBody = 'The selected API doesn\'t exist.'
      }
    }

    return (
      <PageWithSidebar
        sidebarContent={<ApisMenu path={this.props.match} activateFirst={true} />}
        SidebarPusherProps={{ className: 'swagger-section' }}
      >
        <div className='swagger-ui-wrap' ref={this.containerRef} style={{ padding: '0 20px' }}>
          {errorHeader && errorBody && (
            <>
              <Header as='h2' icon textAlign='center' style={{ padding: '40px 0px' }}>
                <Icon name='warning sign' circular />
                <Header.Content>{errorHeader}</Header.Content>
              </Header>
              <Container text textAlign='justified'>
                <p>{errorBody}</p>
              </Container>
            </>
          )}
        </div>
      </PageWithSidebar>
    )
  }
})
